import PropTypes from 'prop-types';
import { useMemo } from 'react';
// @mui
import { CssBaseline } from '@mui/material';
import {
    ThemeProvider as MUIThemeProvider,
    createTheme,
    StyledEngineProvider
} from '@mui/material/styles';
//
import { createPalette } from './palette';
import shadows from './shadows';
import typography from './typography';
import GlobalStyles from './globalStyles';
import customShadows from './customShadows';
import componentsOverride from './overrides';
import { RADIUS, SIZE } from './tokens';
import config from '../config';

// ----------------------------------------------------------------------

ThemeProvider.propTypes = {
    children: PropTypes.node,
    mode: PropTypes.oneOf(['light', 'dark'])
};

export default function ThemeProvider({ children, mode = 'light' }) {
    const rootElement = document.getElementById("root");

    const themeOptions = useMemo(
        () => {
            // Shadows are derived from the palette rather than imported
            // ready-made, so they can be tinted per mode.
            const activePalette = { ...createPalette(mode), ...config.palette };

            return {
                palette: activePalette,
                // `borderRadius` is MUI's own; the rest are the Base radius
                // and size tokens, read as theme.shape.card,
                // theme.sizes.toolbarControlHeight and so on.
                shape: {
                    borderRadius: RADIUS['radius-base'],
                    option: RADIUS['radius-option'],
                    popover: RADIUS['radius-popover'],
                    menuItem: RADIUS['radius-menu-item'],
                    card: RADIUS['radius-card'],
                    toolbarControl: RADIUS['radius-toolbar-control'],
                    toolbarCard: RADIUS['radius-toolbar-card'],
                    pill: RADIUS['radius-pill']
                },
                sizes: {
                    toolbarControlHeight: SIZE['toolbar-control-height'],
                    toolbarCardMinHeight: SIZE['toolbar-card-min-height'],
                    toolbarBadgeHeight: SIZE['toolbar-badge-height'],
                    toolbarResetWidth: SIZE['toolbar-reset-width'],
                    toolbarResetWidthCompact: SIZE['toolbar-reset-width-compact'],
                    buttonHeightLarge: SIZE['button-height-large'],
                    labelHeight: SIZE['label-height'],
                    touchTarget: SIZE['touch-target'],
                    mobileCardPadding: SIZE['mobile-card-padding'],
                    mobileCardGap: SIZE['mobile-card-gap'],
                    mobileSheetRadius: SIZE['mobile-sheet-radius']
                },
                typography,
                shadows: shadows(activePalette),
                customShadows: customShadows(activePalette)
            };
        },
        [mode]
    );

    const theme = createTheme(themeOptions);
    const overrides = componentsOverride(theme);
    // Portals mount inside #root, so Tailwind's `#root` scoping reaches them.
    // Merged into the overrides rather than replacing them, or the Popover
    // and Dialog styleOverrides would be dropped.
    const portalled = (name) => ({
        ...overrides[name],
        defaultProps: {
            ...overrides[name]?.defaultProps,
            container: rootElement
        }
    });
    theme.components = {
        ...overrides,
        MuiPopover: portalled('MuiPopover'),
        MuiPopper: portalled('MuiPopper'),
        MuiDialog: portalled('MuiDialog'),
        MuiModal: portalled('MuiModal')
    };
    return (
        <StyledEngineProvider injectFirst>
            <MUIThemeProvider theme={theme}>
                {/* color-scheme follows the mode, so native scrollbars and
                    form controls go dark too instead of a bright white track. */}
                <CssBaseline enableColorScheme />
                <GlobalStyles />
                {children}
            </MUIThemeProvider>
        </StyledEngineProvider>
    );
}
